import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { usePersistentState } from '../../lib/usePersistentState';
import { CustomerFleetSummary, TriageReason } from '../../types';
import { StatusBadge } from '../../components/common/StatusBadge';
import { Link } from 'react-router-dom';
import {
  Search,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Building,
  Filter,
  Phone,
  ArrowRight,
} from 'lucide-react';

// The real offline reasons, with an honest next step for each. Same classification
// as the triage board (the backend shares one expression), so the two never
// disagree. No invented telemetry, no fixes we cannot perform.
const REASON_META: Record<TriageReason, { label: string; tone: string; action: string }> = {
  needs_reactivation: { label: 'Dead', tone: 'danger', action: 'Reactivate / call owner' },
  unresolved: { label: 'No UID', tone: 'warning', action: 'Resolve UID' },
  reachable_quiet: { label: 'Quiet', tone: 'success', action: 'None — alive, no motion' },
  never_reported: { label: 'Never seen', tone: 'purple', action: 'Call owner / onboarding' },
  silent_unknown: { label: 'Unprobed', tone: 'info', action: 'Re-probe presence' },
};

const relTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '—';
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  const h = Math.floor(s / 3600);
  return h < 48 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};

export const CustomerFleetPage: React.FC = () => {
  const [fleets, setFleets] = useState<CustomerFleetSummary[]>([]);
  const [search, setSearch] = usePersistentState('customerFleet.search', '');
  const [filterDownOnly, setFilterDownOnly] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState<Awaited<ReturnType<typeof api.getFleetTotals>> | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [f, t] = await Promise.all([api.getCustomerFleets(), api.getFleetTotals().catch(() => null)]);
        setFleets(f);
        setTotals(t);
      } catch (err) {
        console.error('Failed to load customer fleets', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (id: string) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  // A site's offline cameras split into: quiet (eye4-reachable, no motion — not a
  // real outage), needs-attention (dead / no-UID / never-reported), and unprobed
  // (offline, eye4 not checked yet). Derived from the per-camera reasons.
  const split = (f: CustomerFleetSummary) => {
    let quiet = 0, attention = 0, unprobed = 0;
    for (const d of f.offlineDevices) {
      if (d.reason === 'reachable_quiet') quiet++;
      else if (d.reason === 'silent_unknown') unprobed++;
      else attention++; // needs_reactivation | unresolved | never_reported (or unknown)
    }
    return { quiet, attention, unprobed };
  };

  const filtered = fleets.filter((f) => {
    const q = search.toLowerCase();
    const matches = f.customerName.toLowerCase().includes(q) || f.customerEmail.toLowerCase().includes(q);
    // "Needs attention" filter hides sites whose offline cameras are only quiet.
    return filterDownOnly ? matches && split(f).attention > 0 : matches;
  });

  // Header uses the real fleet-wide totals (not a sum of the fetched page, which
  // is sorted worst-first and would read a misleading "0 online").
  const online = totals?.online ?? fleets.reduce((a, f) => a + f.onlineCameras, 0);
  const quiet = totals?.silentReachable ?? 0;
  const attention = totals ? totals.inactivated + totals.silentDead + totals.neverReported : 0;

  return (
    <div>
      {/* Aggregates */}
      <div style={{ background: 'var(--bg-surface-card)', border: '1px solid var(--border-dim)', borderRadius: 'var(--radius-lg)', padding: '18px 24px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-main)' }}>Customer Fleet Health</h2>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
            Grouped by customer. A camera that's quiet (reachable, no motion) is not counted as an outage — only cameras that truly need attention are.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 20 }}>
          <Stat label="Online" value={online} color="var(--success-text)" />
          <Stat label="Quiet" value={quiet} color="var(--text-muted)" divider />
          <Stat label="Need attention" value={attention} color="var(--danger-text)" divider />
        </div>
      </div>

      {/* Search + filter */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div className="search-input-wrapper">
          <Search size={15} />
          <input type="text" className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customer name or email..." />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link to="/offline-triage" className="btn btn-ghost btn-sm">
            <span>Triage board</span> <ArrowRight size={13} />
          </Link>
          <button className={`btn btn-sm ${filterDownOnly ? 'btn-danger' : 'btn-secondary'}`} onClick={() => setFilterDownOnly(!filterDownOnly)}>
            <Filter size={13} /> <span>{filterDownOnly ? 'Showing needs-attention' : 'Needs attention only'}</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map((fleet) => {
            const isOpen = !!expanded[fleet.customerId];
            const s = split(fleet);
            // A site is only "critical" when everything is truly down (no online,
            // nothing merely quiet). Some attention items with others alive/quiet is
            // a warning. Only quiet/unprobed offline (no attention) is not an outage.
            const badge = s.attention > 0
              ? (fleet.onlineCameras === 0 && s.quiet === 0 && s.unprobed === 0 ? 'critical' : 'warning')
              : (s.quiet > 0 || s.unprobed > 0 ? 'quiet' : 'online');
            const borderColor = s.attention > 0 ? 'var(--danger-border)' : 'var(--border-dim)';
            return (
              <div key={fleet.customerId} style={{ background: 'var(--bg-surface-card)', border: `1px solid ${borderColor}`, borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
                <div onClick={() => toggle(fleet.customerId)} style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: isOpen ? 'var(--bg-surface)' : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                    {isOpen ? <ChevronDown size={18} color="var(--text-dim)" /> : <ChevronRight size={18} color="var(--text-dim)" />}
                    <Building size={18} color="var(--primary-light)" />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <strong style={{ fontSize: 14.5, color: 'var(--text-main)' }}>{fleet.customerName}</strong>
                        {fleet.planName && fleet.planName !== '—' && (
                          <span style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>({fleet.planName})</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {fleet.customerEmail}{fleet.customerPhone ? ` • ${fleet.customerPhone}` : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600 }}>
                      <div>
                        <span style={{ color: 'var(--success-text)' }}>{fleet.onlineCameras} online</span>
                        {s.quiet > 0 && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>• {s.quiet} quiet</span>}
                        {s.attention > 0 && <span style={{ color: 'var(--danger-text)', marginLeft: 8 }}>• {s.attention} need attention</span>}
                        {s.unprobed > 0 && <span style={{ color: 'var(--info-text)', marginLeft: 8 }}>• {s.unprobed} unprobed</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 400 }}>{fleet.totalCameras} cameras total</div>
                    </div>
                    <StatusBadge status={badge} />
                  </div>
                </div>

                {isOpen && (
                  <div style={{ borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface)' }}>
                    {fleet.offlineDevices.length === 0 ? (
                      <div style={{ padding: '16px 24px', fontSize: 12.5, color: 'var(--success-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CheckCircle2 size={16} /> All {fleet.totalCameras} cameras online.
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 640 }}>
                          <thead>
                            <tr style={{ textAlign: 'left', color: 'var(--text-dim)', borderBottom: '1px solid var(--border-subtle)' }}>
                              <th style={th}>Camera</th>
                              <th style={th}>Serial</th>
                              <th style={th}>Reason</th>
                              <th style={th}>Next step</th>
                              <th style={th}>Last seen</th>
                              <th style={{ ...th, textAlign: 'right' }}>Call</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fleet.offlineDevices.map((dev) => {
                              const meta = dev.reason ? REASON_META[dev.reason] : undefined;
                              return (
                                <tr key={dev.deviceId} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                                  <td style={{ ...td, fontWeight: 500 }}>{dev.name || '(unnamed)'}</td>
                                  <td style={td}><code style={{ color: 'var(--primary-light)', fontSize: 11.5 }}>{dev.serial}</code></td>
                                  <td style={td}>
                                    {meta ? (
                                      <span style={{ background: `var(--${meta.tone}-bg)`, border: `1px solid var(--${meta.tone}-border)`, color: `var(--${meta.tone}-text)`, borderRadius: 999, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>{meta.label}</span>
                                    ) : '—'}
                                  </td>
                                  <td style={{ ...td, color: 'var(--text-muted)' }}>{meta?.action ?? '—'}</td>
                                  <td style={{ ...td, color: 'var(--text-dim)' }}>{relTime(dev.lastSeenAt)}</td>
                                  <td style={{ ...td, textAlign: 'right' }}>
                                    {dev.namePhone ? (
                                      <a href={`tel:${dev.namePhone}`} className="btn btn-secondary btn-sm" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
                                        <Phone size={12} /> <span>{dev.namePhone}</span>
                                      </a>
                                    ) : (
                                      <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No customers match.</div>
          )}
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; color: string; divider?: boolean }> = ({ label, value, color, divider }) => (
  <div style={divider ? { borderLeft: '1px solid var(--border-subtle)', paddingLeft: 20 } : undefined}>
    <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);

const th: React.CSSProperties = { padding: '8px 12px', fontWeight: 600 };
const td: React.CSSProperties = { padding: '10px 12px' };
